import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Printer, FileText, XCircle, HandCoins, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ModalShell } from "../components/ModalShell";
import api, { fmtGNF, fmtDate, apiError, openPdf, logError, openDocument } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { AmountInput } from "../components/AmountInput";

function SaleStatusBadge({ sale }) {
  if (sale.status === "cancelled") {
    return <span className="badge-unpaid px-2.5 py-1 rounded-full text-xs font-semibold">Annulée</span>;
  }
  if (sale.payment === "cash") return <StatusBadge status="paid" />;
  return <StatusBadge status={sale.payment_status} remaining={sale.total - sale.amount_paid} fmt={fmtGNF} />;
}

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [cancelId, setCancelId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [payId, setPayId] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [blSale, setBlSale] = useState(null);
  const [bl, setBl] = useState({ show_prices: false, received_by: "", note: "" });
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/sales").then((r) => setSales(r.data)).catch((e) => logError(e)), []);
  useEffect(() => { load(); }, [load]);

  const doCancel = async () => {
    try {
      await api.post(`/sales/${cancelId}/cancel`, { reason: cancelReason });
      toast.success("Vente annulée (tracée) — stock remis à jour");
      setCancelId(null); setCancelReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doPay = async () => {
    try {
      const r = await api.post(`/sales/${payId}/pay`, { amount: parseFloat(payAmount) });
      toast.success(r.data.payment_status === "paid" ? "Créance soldée ✓" : `Encaissé — reste ${fmtGNF(r.data.remaining)}`);
      setPayId(null); setPayAmount(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const createDeliveryNote = async () => {
    try {
      const { data } = await api.post(`/sales/${blSale.id}/delivery-note`, bl);
      toast.success(`Bon de livraison ${data.number} créé — aucun effet sur le stock ni les finances`);
      setBlSale(null);
      await openDocument("delivery_note", data.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="sales-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-2xl">Ventes</h1>
        <button data-testid="new-sale-btn-sales" onClick={() => navigate("/ventes/nouvelle")} className="btn-amber py-2.5 px-5 flex items-center gap-2 text-sm">
          <Plus size={16} weight="bold" /> Nouvelle vente
        </button>
      </div>

      {sales.length === 0 && <div className="kcard p-8 text-center text-sm text-gray-400">Aucune vente pour l'instant. Cliquez sur « Nouvelle vente » pour commencer.</div>}

      <div className="space-y-2.5">
        {sales.map((s) => (
          <div key={s.id} data-testid="sale-row" className={`kcard p-4 ${s.status === "cancelled" ? "opacity-60" : ""}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="font-mono-doc text-sm font-semibold">{s.number}</span>
              <span className="text-xs text-gray-400">{fmtDate(s.created_at)}</span>
              <span className="flex-1" />
              <span className="font-display font-bold tabular-nums">{fmtGNF(s.total)}</span>
              <SaleStatusBadge sale={s} />
            </div>
            <p className="text-sm text-gray-600 mt-1.5">
              {s.items?.length ? s.items.map((i) => `${i.name} ×${i.qty}`).join(", ") : (s.category || "Encaissement")}
              {s.client_name ? ` — ${s.client_name} (${s.client_phone})` : ""}
              <span className="text-gray-400"> · par {s.author_name}</span>
            </p>
            {s.status === "cancelled" && <p className="text-xs text-[#B4432F] mt-1">Annulée par {s.cancelled_by} — motif : {s.cancel_reason}</p>}
            {s.status === "active" && (
              <div className="flex flex-wrap gap-2 mt-3">
                <button data-testid="sale-ticket-btn" onClick={() => openPdf(s.id, "ticket")} className="btn-ghost text-xs py-1.5 px-3 flex items-center gap-1"><Printer size={14} /> Ticket</button>
                <button data-testid="sale-invoice-btn" onClick={() => openPdf(s.id, "invoice")} className="btn-ghost text-xs py-1.5 px-3 flex items-center gap-1"><FileText size={14} /> Facture</button>
                {s.payment === "credit" && s.payment_status !== "paid" && (
                  <button data-testid="sale-pay-btn" onClick={() => { setPayId(s.id); setPayAmount(""); }} className="btn-ghost text-xs py-1.5 px-3 flex items-center gap-1 text-[#2E7D5B]"><HandCoins size={14} /> Encaisser</button>
                )}
                <button data-testid="sale-delivery-btn" onClick={async () => {
                  const { data } = await api.get("/company").catch(() => ({ data: {} }));
                  setBl({ show_prices: false, received_by: "", note: data.document_note || "" });
                  setBlSale(s);
                }} className="btn-ghost text-xs py-1.5 px-3 flex items-center gap-1"><Truck size={14} /> Bon de livraison</button>
                <button data-testid="sale-cancel-btn" onClick={() => { setCancelId(s.id); setCancelReason(""); }} className="btn-ghost text-xs py-1.5 px-3 flex items-center gap-1 text-[#B4432F]"><XCircle size={14} /> Annuler</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {cancelId && (
        <ModalShell size="max-w-sm" onClose={() => setCancelId(null)}>
            <h3 className="font-display font-bold text-lg mb-2">Annuler la vente</h3>
            <p className="text-sm text-gray-500 mb-3">Le motif est obligatoire. L'annulation sera tracée (auteur, date, motif) et le stock remis à jour.</p>
            <textarea data-testid="cancel-reason-input" className="kinput mb-3" rows={2} placeholder="Motif de l'annulation" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2.5" onClick={() => setCancelId(null)}>Retour</button>
              <button data-testid="cancel-confirm-btn" className="btn-amber flex-1 py-2.5" disabled={!cancelReason.trim()} onClick={doCancel}>Confirmer</button>
            </div>
          </ModalShell>
      )}

      {payId && (
        <ModalShell size="max-w-sm" onClose={() => setPayId(null)}>
            <h3 className="font-display font-bold text-lg mb-3">Encaisser un règlement</h3>
            <AmountInput data-testid="pay-amount-input" onOverflow={(m) => toast.error(m)} className="mb-3" placeholder="Montant reçu (GNF)" value={payAmount} onChange={setPayAmount} />
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2.5" onClick={() => setPayId(null)}>Retour</button>
              <button data-testid="pay-confirm-btn" className="btn-amber flex-1 py-2.5" disabled={!payAmount} onClick={doPay}>Encaisser</button>
            </div>
          </ModalShell>
      )}
      {blSale && (
        <ModalShell size="max-w-sm" testid="delivery-note-modal" onClose={() => setBlSale(null)}>
          <h3 className="font-display font-bold text-lg">Bon de livraison — vente {blSale.number}</h3>
          <p className="text-xs text-gray-500">Aucun effet sur le stock ni les finances : la vente a déjà tout mis à jour.</p>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="bl-show-prices" type="checkbox" checked={bl.show_prices} onChange={(e) => setBl({ ...bl, show_prices: e.target.checked })} />
            Afficher les prix sur le bon
          </label>
          <input data-testid="bl-received-by" className="kinput" placeholder="Reçu par (nom, optionnel)" value={bl.received_by} onChange={(e) => setBl({ ...bl, received_by: e.target.value })} />
          <textarea data-testid="bl-note" className="kinput" rows={3} placeholder="Note (la note par défaut s'applique si vide)" value={bl.note} onChange={(e) => setBl({ ...bl, note: e.target.value })} />
          <div className="flex gap-2">
            <button className="btn-ghost flex-1 py-2.5" onClick={() => setBlSale(null)}>Annuler</button>
            <button data-testid="bl-submit-btn" className="btn-amber flex-1 py-2.5" onClick={createDeliveryNote}>Créer le bon</button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
